import Image from "next/image";

/**
 * F9 (Sprint 15) — quiz finishing-state progress UI.
 *
 * Extracted from the inline finishing markup that shipped with F8
 * (Sprint 14). The 60s server-side minimum (MIN_DURATION_MS) is honored
 * client-side; rather than a single static caption with a motionless sparrow
 * (the 2026-06-13 "frozen page" symptom), the finishing stage shows a
 * rotating reasoning-step caption + a progress bar that fills 0→95% linearly
 * across the wait.
 *
 * Component split (judgment call): the elapsed-time timer stays in
 * QuizFinishingState inside quiz-player.tsx so the QuizShell header bar and
 * this inner bar are driven by ONE interval (no duplicate timer, no drift,
 * and the existing single-interval source guard in quiz-player.test.tsx stays
 * valid). This component is therefore presentational — it receives the
 * already-derived `caption` and `progressPct` rather than a `startedAt`
 * timestamp. The pure timing math (12 captions, rotation + clamp, 0→95%
 * linear fill) lives in and is unit-tested at lib/quiz/finishing-state.ts.
 *
 * SSR-safe: purely a function of its props; the parent renders it at
 * progressPct=0 / first caption on first paint and only advances after mount.
 *
 * F4 ties in: the sparrow wears the "thinking/computing" .s5-sparrow-thinking
 * state (scale pulse + brightness micro-blink), dropped under reduced motion.
 *
 * `aria-live="polite"` on the caption announces each rotation to AT; the bar
 * carries role="progressbar" with a live aria-valuenow.
 */

interface Props {
  /** Caption to show now (from captionForElapsed). */
  caption: string;
  /** Bar fill 0–100 (from progressForElapsed; tops out at 95 until routing). */
  progressPct: number;
  /** Drops the bar's width transition + sparrow animation when true. */
  reduceMotion: boolean;
  /** Non-null when /api/quiz/finish failed; renders the retry affordance. */
  finishError: string | null;
  /** Re-attempt the finish POST. */
  onRetry: () => void;
}

const SPARROW_SRC = "/illustrations/sparrow.png";

export function FinishingProgress({
  caption,
  progressPct,
  reduceMotion,
  finishError,
  onRetry,
}: Props) {
  return (
    <div className="flex min-h-[60dvh] flex-col items-center justify-center text-center">
      <Image
        src={SPARROW_SRC}
        alt=""
        width={128}
        height={128}
        className={`quiz-sparrow ${reduceMotion ? "" : "s5-sparrow-thinking"}`}
        priority
      />
      <p
        className="mt-6 min-h-[1.5em] max-w-prose text-sub text-mute"
        aria-live="polite"
      >
        {caption}
      </p>
      <div className="mt-6 w-full max-w-column">
        <div
          className="progress-track"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progressPct}
          aria-label="Preparing your result"
        >
          <span
            className={`progress-fill ${reduceMotion ? "" : "transition-[width] duration-700 ease-out"}`}
            style={{ width: `${progressPct}%` }}
            aria-hidden="true"
          />
        </div>
      </div>
      {finishError ? (
        <p className="mt-6 max-w-prose text-sub text-mute" aria-live="polite">
          We couldn’t load your result just now.{" "}
          <button type="button" onClick={onRetry} className="text-teal underline">
            Try again
          </button>
          .
        </p>
      ) : null}
    </div>
  );
}
